import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KpiHistoryService } from '../../services/kpi-history.service';
import { KpiHistorySearchDto } from '../../models/kpi-history-search-dto';
import dayjs from 'dayjs';
import { Message } from 'primeng/message';
import { Direction, HistoryKpiCardData } from './components/models/history-kpi-card-data';
import { HistoryKpiCardComponent } from './components/history-kpi-card/history-kpi-card.component';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { CalendarFilterMapper } from '@myarvato/shared/calendar';
import { FilterRecord } from '@myarvato/shared/models';

@Component({
  selector: 'transport-key-indicators',
  imports: [SkeletonModule, Message, HistoryKpiCardComponent, TranslatePipe],
  templateUrl: './key-indicators.component.html',
  styleUrl: './key-indicators.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeyIndicatorsComponent implements OnInit, OnDestroy {
  // Injections
  private readonly translateService = inject(TranslateService);
  private readonly kpiHistoryService = inject(KpiHistoryService);
  private readonly customizationTranslationService = inject(CustomizationTranslationService);

  // Fields
  private readonly periodLengthDays = 14;
  private readonly startRequestDate = dayjs()
    .startOf('day')
    .subtract(this.periodLengthDays - 1, 'day')
    .toDate();
  private readonly endRequestDate = dayjs().endOf('day').toDate();

  private readonly totalKpiName: string = 'Total';
  private readonly issueKpiName: string = 'Exception';
  private readonly delayedKpiName: string = 'Delayed';

  // Change detection
  protected readonly state = signal<{
    loading: boolean;
    error: unknown | undefined;
    data: Record<string, Record<string, number>> | undefined;
  }>({
    loading: true,
    error: undefined,
    data: undefined,
  });

  protected readonly isOnError = computed(() => this.state().error !== undefined);
  protected readonly isSuccess = computed(() => this.state().data !== undefined && !this.isOnError());
  protected readonly isLoading = computed(() => this.state().loading);

  protected readonly totalKpiCardData: Signal<HistoryKpiCardData> = computed(() => {
    const stateData = this.state().data ?? {};

    const filter = CalendarFilterMapper.getDateRangeFilter({ from: this.startRequestDate, to: this.endRequestDate }, 'ShippingDate');

    const title = this.translateService.instant('pages.transport.tracking-overview.widgets.key-indicators.kpi-cards.total.title');
    return this.buildKpiCardData(stateData, this.totalKpiName, title, '#B0C800', '#FEFEFB', undefined, filter);
  });

  protected readonly issueKpiCardData: Signal<HistoryKpiCardData> = computed(() => {
    const stateData = this.state().data ?? {};

    const filter = CalendarFilterMapper.getDateRangeFilter({ from: this.startRequestDate, to: this.endRequestDate }, 'ShippingDate');
    filter['Severity'] = [{ value: ['EXCEPTION'], matchMode: 'in', operator: 'or' }];

    const lastTotalValue = this.getLastKpiValueOfRange(stateData, this.totalKpiName, this.startRequestDate, this.endRequestDate);

    const title = this.translateService.instant('pages.transport.tracking-overview.widgets.key-indicators.kpi-cards.exception.title');
    return this.buildKpiCardData(
      stateData,
      this.issueKpiName,
      title,
      '#B6000D',
      '#FEFBFB',
      lastTotalValue,
      filter,
      Direction.DECREASE_IS_GOOD,
    );
  });

  protected readonly delayedKpiCardData: Signal<HistoryKpiCardData> = computed(() => {
    const stateData = this.state().data ?? {};

    const filter = CalendarFilterMapper.getDateRangeFilter({ from: this.startRequestDate, to: this.endRequestDate }, 'ShippingDate');
    filter['DaysDelayed'] = [{ value: '0', matchMode: 'gt', operator: 'or' }];

    const lastTotalValue = this.getLastKpiValueOfRange(stateData, this.totalKpiName, this.startRequestDate, this.endRequestDate);

    const title = this.translateService.instant('pages.transport.tracking-overview.widgets.key-indicators.kpi-cards.delayed.title');
    return this.buildKpiCardData(
      stateData,
      this.delayedKpiName,
      title,
      '#589DBF',
      '#FCFDFE',
      lastTotalValue,
      filter,
      Direction.DECREASE_IS_GOOD,
    );
  });

  private buildKpiCardData(
    kpiData: Record<string, Record<string, number>>,
    kpiName: string,
    title: string,
    chartLineColor: string,
    chartFillColor: string,
    valueComparison: number | undefined = undefined,
    listFilter: FilterRecord | undefined = undefined,
    kpiSemanticDirection: Direction = Direction.INCREASE_IS_GOOD,
  ): HistoryKpiCardData {
    const kpiSerialRange = this.getKpiSerialRange(kpiData, kpiName, this.startRequestDate, this.endRequestDate);

    const lastValue = this.getLastKpiValueOfRange(kpiData, kpiName, this.startRequestDate, this.endRequestDate);
    const previousValue = this.getLastKpiValueOfRange(
      kpiData,
      kpiName,
      this.startRequestDate,
      dayjs(this.endRequestDate).subtract(1, 'day').toDate(),
    );

    return {
      title,
      value: lastValue,
      valueComparison: valueComparison,
      previousValue: previousValue,
      data: kpiSerialRange,
      chartLineColor,
      chartFillColor,
      listFilter,
      kpiSemanticDirection,
    };
  }

  protected readonly errorMessage = computed(() => {
    const exception = this.state().error as { error: { errorCode: string } };
    const errorCode = exception?.error?.errorCode || 'G0400';
    const errorTranslationTitle = this.customizationTranslationService.getTranslation(`errors.${errorCode}.title`, 'errors.G0400.title');
    const errorTranslationDescription = this.customizationTranslationService.getTranslation(
      `errors.${errorCode}.description`,
      'errors.G0400.description',
    );

    return {
      title: errorTranslationTitle,
      description: errorTranslationDescription,
    };
  });

  public ngOnInit(): void {
    const filter: KpiHistorySearchDto = {
      start: this.startRequestDate,
      end: this.endRequestDate,
    };

    this.kpiHistoryService.getCachedKpis(filter).subscribe({
      next: (data) => {
        this.state.set({ loading: false, error: undefined, data: data.kpiHistoryData });
      },
      error: (error) => {
        console.error(error);
        this.state.set({
          loading: false,
          error,
          data: undefined,
        });
      },
    });
  }

  private getLastKpiValueOfRange(kpiData: Record<string, Record<string, number>>, kpiName: string, startDate: Date, endDate: Date) {
    const kpiRange = this.getKpiSerialRange(kpiData, kpiName, startDate, endDate);

    const values = Object.values(kpiRange);

    return values[values.length - 1];
  }

  private getKpiSerialRange(
    kpiData: Record<string, Record<string, number>>,
    kpiName: string,
    startDate: Date,
    endDate: Date,
  ): Record<string, number> {
    const totalRecords: Record<string, number> = {};

    for (let date = dayjs(startDate); date.isBefore(dayjs(endDate)) || date.isSame(dayjs(endDate), 'day'); date = date.add(1, 'day')) {
      const dateKey = date.format('YYYY-MM-DD');
      totalRecords[dateKey] = 0; // 0 as default value if no data found

      const dataForDate = kpiData ? kpiData[dateKey] : undefined;
      if (dataForDate) {
        totalRecords[dateKey] = dataForDate[kpiName];
      }
    }

    return totalRecords;
  }

  ngOnDestroy(): void {
    this.kpiHistoryService.invalidateCache();
  }
}
